import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Clock, ExternalLink, FileUp, Loader2, ShieldAlert, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

const IRS_W9 = "https://www.irs.gov/pub/irs-pdf/fw9.pdf";
const STATE = {
  missing: ["W-9 needed before your first payout", "Download the IRS W-9, fill it in, sign it and upload the PDF. Your payouts stay on hold until we approve it.", "border-amber-300 bg-amber-50", ShieldAlert, "text-amber-600"],
  pending: ["W-9 under review", "Thanks — we're reviewing your W-9. Payouts are released once it's approved.", "border-slate-200 bg-white", Clock, "text-slate-500"],
  rejected: ["Please re-upload your W-9", "We couldn't approve your last upload. Upload a corrected, signed copy.", "border-red-300 bg-red-50", ShieldAlert, "text-red-600"],
  approved: ["W-9 on file", "Your W-9 is approved. Payouts are released monthly.", "border-emerald-200 bg-emerald-50/50", ShieldCheck, "text-emerald-600"],
};

export function ProW9Card() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const load = () => api.get("/pro/w9").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const [title, body, cls, Icon, ic] = STATE[d.status] || STATE.missing;
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { await api.post("/pro/w9", fd); toast.success("W-9 uploaded"); load(); } catch (e2) { toast.error(formatErr(e2)); } finally { setBusy(false); }
  };
  return (
    <div className={`border rounded-lg p-5 flex flex-wrap items-center gap-4 ${cls}`} data-testid="pro-w9-card">
      <Icon className={`h-6 w-6 ${ic}`} />
      <div className="flex-1 min-w-[220px]">
        <div className="font-semibold text-slate-900" data-testid="pro-w9-status">{title}</div>
        <p className="text-xs text-slate-600 mt-0.5">{body}</p>
        {d.status === "rejected" && d.latest?.reason && <p className="text-xs text-red-700 mt-1" data-testid="pro-w9-reason">Note: {d.latest.reason}</p>}
      </div>
      {d.status !== "approved" && (
        <div className="flex gap-2">
          <a href={IRS_W9} target="_blank" rel="noreferrer" data-testid="pro-w9-irs-link"><Button variant="outline" size="sm"><ExternalLink className="h-4 w-4 mr-1" />IRS W-9</Button></a>
          <input ref={ref} type="file" accept="application/pdf,.pdf" className="hidden" onChange={upload} data-testid="pro-w9-file-input" />
          <Button size="sm" onClick={() => ref.current?.click()} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="pro-w9-upload-button">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileUp className="h-4 w-4 mr-1" />}{d.status === "pending" ? "Replace PDF" : "Upload signed PDF"}</Button>
        </div>
      )}
    </div>
  );
}

function Row({ f, onDone }) {
  const [busy, setBusy] = useState(false);
  const view = async () => {
    try {
      const res = await api.get(`/admin/w9/${f.w9_id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank", "noopener");
    } catch { toast.error("Couldn't open the W-9"); }
  };
  const act = async (kind) => {
    let body = {};
    if (kind === "reject") { const reason = window.prompt("Reason shown to the pro (optional):", ""); if (reason === null) return; body = { reason }; }
    setBusy(true);
    try { await api.post(`/admin/w9/${f.w9_id}/${kind}`, body); toast.success(kind === "approve" ? "W-9 approved — payouts released" : "W-9 rejected"); window.dispatchEvent(new Event("gv:w9-changed")); onDone(); }
    catch (e) { toast.error(formatErr(e)); setBusy(false); }
  };
  return (
    <div className="px-5 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`w9-row-${f.w9_id}`}>
      <div className="flex-1 min-w-0"><div className="font-semibold text-slate-900">{f.pro_name}</div><div className="text-xs text-slate-500">{f.pro_email} · uploaded {new Date(f.uploaded_at).toLocaleString()} · <span className="uppercase font-mono">{f.status}</span></div></div>
      <Button size="sm" variant="outline" onClick={view} data-testid={`w9-view-${f.w9_id}`}>View PDF</Button>
      {f.status === "pending" && <>
        <Button size="sm" onClick={() => act("approve")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`w9-approve-${f.w9_id}`}><Check className="h-4 w-4 mr-1" />Approve</Button>
        <Button size="sm" variant="outline" onClick={() => act("reject")} disabled={busy} data-testid={`w9-reject-${f.w9_id}`}><X className="h-4 w-4 mr-1" />Reject</Button>
      </>}
    </div>
  );
}

export function W9Review() {
  const [status, setStatus] = useState("pending");
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/w9", { params: { status } }).then((r) => setD(r.data)).catch(() => setD({ forms: [], missing: [] })), [status]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="w9-review">
      <div className="p-4 flex flex-wrap gap-2 items-center border-b border-slate-100">
        {["pending", "approved", "rejected", "all"].map((s) => <button key={s} onClick={() => setStatus(s)} data-testid={`w9-filter-${s}`} className={`px-3 py-1 rounded-full text-xs border capitalize ${status === s ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600"}`}>{s}</button>)}
        {d?.missing?.length > 0 && <span className="ml-auto text-xs text-amber-700" data-testid="w9-missing-count">{d.missing.length} active pro{d.missing.length === 1 ? " has" : "s have"} no W-9 yet: {d.missing.slice(0, 4).map((m) => m.name).join(", ")}{d.missing.length > 4 ? "…" : ""}</span>}
      </div>
      <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
        {!d ? <Loader2 className="h-5 w-5 m-5 animate-spin text-emerald-600" /> : d.forms.length === 0 ? <p className="p-5 text-sm text-slate-500" data-testid="w9-empty">No {status === "all" ? "" : status} W-9s.</p> : d.forms.map((f) => <Row key={f.w9_id} f={f} onDone={load} />)}
      </div>
    </div>
  );
}
